import { Backdrop, Box, Dialog, IconButton } from "@mui/material";
import {
  resetSlider,
  selectorSliderItems,
  selectorSliderOpen,
  selectorSliderStartIndex,
} from "../../store/slices/sliderSlice";
import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
} from "framer-motion";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const wrapValue = (minValue, maxValue, currentValue) => {
  const range = maxValue - minValue;
  if (range <= 0) return minValue;
  return ((((currentValue - minValue) % range) + range) % range) + minValue;
};

const CmkBackdropSlider = () => {
  const dispatch = useDispatch();
  const open = useSelector(selectorSliderOpen);
  const items = useSelector(selectorSliderItems);
  const startIndex = useSelector(selectorSliderStartIndex);
  const [_open, _setOpen] = useState(false);
  const [[page, direction], setPage] = useState([startIndex, 0]);
  const scale = useMotionValue(1);
  const springScale = useSpring(scale, { stiffness: 300, damping: 30 });
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const containerRef = useRef(null);
  const [canPan, setCanPan] = useState(false);
  scale.on("change", (v) => {
    if (Boolean(v === 1)) {
      x.set(0);
      y.set(0);
    }
    setCanPan(Boolean(v > 1));
  });

  // scale.onChange((v) => {
  //   if (Boolean(v === 1)) {
  //     x.set(0);
  //     y.set(0);
  //   }
  //   setCanPan(Boolean(v > 1));
  // });

  const handleClose = () => {
    _setOpen(false);
    dispatch(resetSlider());
  };

  const handleWheelZoom = (e) => {
    e.stopPropagation();
    const zoomFactor = e.deltaY > 0 ? 0.95 : 1.05;
    const newScale = Math.min(Math.max(scale.get() * zoomFactor, 1), 3);
    scale.set(newScale);
    if (newScale === 1) {
      x.set(0);
      y.set(0);
    }
  };

  const handleDoubleClick = () => {
    if (scale.get() === 1) {
      scale.set(2);
    } else {
      scale.set(1);
      x.set(0);
      y.set(0);
    }
  };

  useMemo(() => {
    setPage([startIndex, direction]);
  }, [startIndex]);

  useMemo(() => {
    _setOpen(open);
  }, [open]);

  const imageIndex = wrapValue(0, items.length, page);

  useEffect(() => {
    scale.set(1);
    x.set(0);
    y.set(0);
  }, [imageIndex]);

  if (!_open || _.isEmpty(items)) return null;

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      hideBackdrop={true}
      className="w-full h-full flex flex-col flex-1 py-20 px-24"
      sx={{
        backgroundColor: "#000000e8",
      }}
      classes={{
        paper:
          "!w-full !h-full !max-w-full !max-h-full !bg-transparent !shadow-0 !m-0 !p-0",
      }}
    >
      <Box className={"w-full h-full overflow-hidden flex flex-col flex-1"}>
        <Box className={"w-full flex flex-row items-end justify-end"}>
          <IconButton size={"small"} onClick={handleClose}>
            <i className="feather-x text-lg text-white"></i>
          </IconButton>
        </Box>

        <Box className={"w-full h-full overflow-hidden flex flex-col flex-1"}>
          <Box
            ref={containerRef}
            onWheel={handleWheelZoom}
            onDoubleClick={handleDoubleClick}
            className={clsx(
              "w-full h-full overflow-hidden",
              "relative flex justify-center items-center",
              canPan ? "cursor-grab active:cursor-grabbing" : "cursor-ew-resize"
            )}
          >
            <AnimatePresence initial={false} custom={direction}>
              <motion.img
                key={page}
                src={items[imageIndex]?.picture_src}
                custom={direction}
                variants={variants}
                style={canPan ? { scale: springScale, x, y } : {}}
                className={clsx(
                  "absolute w-full h-full object-contain rounded-8",
                  !items[imageIndex]?.picture_src && "!opacity-30 p-72"
                )}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                // drag="x"
                drag={canPan ? true : "x"}
                // dragConstraints={{ left: 0, right: 0 }}
                dragConstraints={canPan ? {} : { left: 0, right: 0 }}
                // dragElastic={1}
                dragElastic={canPan ? 0.2 : 1}
                onDragEnd={(e, { offset, velocity }) => {
                  if (canPan) return;

                  const swipe = swipePower(offset.x, velocity.x);

                  if (swipe < -swipeConfidenceThreshold) {
                    paginate(1);
                  } else if (swipe > swipeConfidenceThreshold) {
                    paginate(-1);
                  }
                }}
              />
            </AnimatePresence>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default memo(CmkBackdropSlider);
